import { LuChevronRight, LuUsers } from 'react-icons/lu';

import type { GuestSpaceSummary } from '@mds/shared/types/guest-booking';

import MediaCard from './media-card';

/**
 * A space as a card — the one drawing of it on the home rail, the spaces grid
 * and the "Also available" row.
 *
 * It exists because those three were each filling in `MediaCard` by hand and
 * had drifted: one carried a price and a second link, one a bare cue, one a
 * badge. Deciding the card once here is what keeps them the same.
 *
 * Only the card, never its container: drop it into a `CardRail` or a
 * `MediaCardRow` as the section wants.
 */
export default function SpaceCard({ space }: { space: GuestSpaceSummary }) {
  return (
    <MediaCard
      to={`/spaces/${space.slug}`}
      image={space.coverImage ? { src: space.coverImage, alt: space.name } : undefined}
      title={space.name}
      description={space.shortDescription}
      // `null` is a space that is not counted in seats, like the open floor.
      meta={
        space.capacity === null ? undefined : [{ label: `${space.capacity} seats`, icon: LuUsers }]
      }
      footer={
        // A cue, not a link. The card already carries `to`, so a second link
        // to the same slug would be a second tab stop announcing the same
        // destination twice. As a `span` it is the card's link that the click
        // and the cursor belong to — see the pointer rule on the footer slot in
        // `MediaCard`.
        // Gone from the phone's list item, which shows a chevron instead.
        <div className="flex justify-end pt-2 group-data-list:max-sm:hidden">
          <span className="inline-flex items-center gap-1 text-sm font-medium text-brand-text">
            Details
            <LuChevronRight aria-hidden="true" className="size-4 shrink-0" />
          </span>
        </div>
      }
    />
  );
}
